/**
 * Support workspace API (FOR-350, owner only, /api/support/admin). Customer
 * messages are in here, so nothing is persisted to localStorage
 * (meta.persist false). apiClient already returns the response body.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/lib/api"
import {
  listQuery,
  type SupportBlock,
  type SupportBlockType,
  type SupportContext,
  type SupportConversation,
  type SupportConversationList,
  type SupportConversationRow,
  type SupportCounts,
  type SupportListFilters,
  type SupportMessage,
  type SupportMode,
  type SupportProject,
  type SupportProjectCreated,
  type SupportProjectInput,
  type SupportSecret,
  type SupportStatus,
} from "@/lib/support"

const BASE = "/support/admin"

export interface SupportConversationUpdate {
  status?: SupportStatus
  assignee_id?: number | null
  unread?: boolean
  spam?: boolean
  /** With spam: true, also block the sender's address or whole domain. */
  block?: "email" | "domain"
}

export const supportApi = {
  projects: () => apiClient.get<{ data: SupportProject[] }>(`${BASE}/projects`).then((b) => b.data),
  createProject: (body: SupportProjectInput & { slug: string; name: string }) => apiClient.post<SupportProjectCreated>(`${BASE}/projects`, body),
  updateProject: (slug: string, body: SupportProjectInput) =>
    apiClient.patch<{ data: SupportProject }>(`${BASE}/projects/${encodeURIComponent(slug)}`, body).then((b) => b.data),
  /** The new value comes back once; the old one keeps working for 24 hours. */
  rotate: (slug: string, which: SupportSecret) =>
    apiClient.post<Partial<Record<SupportSecret, string>>>(`${BASE}/projects/${encodeURIComponent(slug)}/rotate`, { which }).then((b) => b[which] ?? ""),
  setMode: (slug: string, mode: SupportMode) =>
    apiClient
      .post<{ data: SupportProject }>(`${BASE}/projects/${encodeURIComponent(slug)}/mode`, mode === "live" ? { mode, confirm: true } : { mode })
      .then((b) => b.data),

  /** Sets (or with null clears) the project's own Resend key. Write-only. */
  setResendKey: (slug: string, resendKey: string | null) =>
    apiClient.put(`${BASE}/projects/${encodeURIComponent(slug)}/resend-key`, { resend_key: resendKey }),

  conversations: (filters: SupportListFilters) => apiClient.get<SupportConversationList>(`${BASE}/conversations`, { params: listQuery(filters) }),
  counts: () => apiClient.get<{ data: SupportCounts }>(`${BASE}/counts`).then((b) => b.data),
  /** Opening a conversation marks it read. */
  conversation: (id: string) => apiClient.get<{ data: SupportConversation }>(`${BASE}/conversations/${encodeURIComponent(id)}`).then((b) => b.data),
  update: (id: string, body: SupportConversationUpdate) =>
    apiClient.patch<{ data: SupportConversationRow }>(`${BASE}/conversations/${encodeURIComponent(id)}`, body).then((b) => b.data),
  reply: (id: string, body: string, status?: SupportStatus, files: File[] = []) => {
    const url = `${BASE}/conversations/${encodeURIComponent(id)}/reply`
    if (files.length === 0) {
      return apiClient.post<{ data: SupportMessage }>(url, status ? { body, status } : { body }).then((b) => b.data)
    }
    const form = new FormData()
    form.append("body", body)
    if (status) form.append("status", status)
    for (const file of files) form.append("files[]", file)
    return apiClient.post<{ data: SupportMessage }>(url, form, { headers: { "Content-Type": "multipart/form-data" } }).then((b) => b.data)
  },
  note: (id: string, body: string) =>
    apiClient.post<{ data: SupportMessage }>(`${BASE}/conversations/${encodeURIComponent(id)}/note`, { body }).then((b) => b.data),
  context: (id: string) => apiClient.get<{ data: SupportContext }>(`${BASE}/conversations/${encodeURIComponent(id)}/context`).then((b) => b.data),
  createTask: (id: string, body: { title?: string; board?: string } = {}) =>
    apiClient
      .post<{ data: { task: { id: number; key: string } } }>(`${BASE}/conversations/${encodeURIComponent(id)}/task`, body)
      .then((b) => b.data.task),
  /** Fetches with the session (the route needs it) and hands the file to the browser. */
  downloadAttachment: async (id: string, filename: string) => {
    const blob = await apiClient.get<Blob>(`${BASE}/attachments/${encodeURIComponent(id)}`, { responseType: "blob" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  blocks: () => apiClient.get<{ data: SupportBlock[] }>(`${BASE}/blocks`).then((b) => b.data),
  addBlock: (body: { type: SupportBlockType; value: string; reason?: string; project_id?: number | null }) =>
    apiClient.post<{ data: SupportBlock }>(`${BASE}/blocks`, body).then((b) => b.data),
  removeBlock: (id: number) => apiClient.delete(`${BASE}/blocks/${id}`),
}

export const supportKeys = {
  all: ["support"] as const,
  projects: ["support", "projects"] as const,
  lists: ["support", "conversations"] as const,
  list: (filters: SupportListFilters) => ["support", "conversations", filters] as const,
  counts: ["support", "counts"] as const,
  conversation: (id: string) => ["support", "conversation", id] as const,
  context: (id: string) => ["support", "context", id] as const,
  blocks: ["support", "blocks"] as const,
}

/** Nav badges and the open list look again every minute. */
const POLL_MS = 60_000

export function useSupportCounts(enabled = true) {
  return useQuery({ queryKey: supportKeys.counts, queryFn: supportApi.counts, enabled, refetchInterval: POLL_MS, meta: { persist: false } })
}

export function useSupportProjects(enabled = true) {
  return useQuery({ queryKey: supportKeys.projects, queryFn: supportApi.projects, enabled, staleTime: 30_000, meta: { persist: false } })
}

export function useSupportConversations(filters: SupportListFilters) {
  return useQuery({
    queryKey: supportKeys.list(filters),
    queryFn: () => supportApi.conversations(filters),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    meta: { persist: false },
  })
}

export function useSupportConversation(id: string | undefined) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: supportKeys.conversation(id ?? ""),
    queryFn: async () => {
      const data = await supportApi.conversation(id!)
      // The read cleared its unread flag: when a cached list still shows it
      // unread, the list dot and the nav badge follow (not on every poll).
      const shownUnread = qc
        .getQueriesData<SupportConversationList>({ queryKey: supportKeys.lists })
        .some(([, list]) => list?.data.some((row) => row.id === id && row.unread))
      if (shownUnread) {
        qc.invalidateQueries({ queryKey: supportKeys.lists })
        qc.invalidateQueries({ queryKey: supportKeys.counts })
      }
      return data
    },
    enabled: !!id,
    refetchInterval: POLL_MS,
    meta: { persist: false },
  })
}

/** The product's org panel: fetched only when the panel asks (it calls the product). */
export function useSupportContext(id: string, enabled: boolean) {
  return useQuery({
    queryKey: supportKeys.context(id),
    queryFn: () => supportApi.context(id),
    enabled,
    // The API caches it for five minutes too.
    staleTime: 5 * 60_000,
    retry: false,
    meta: { persist: false },
  })
}

export function useSupportBlocks() {
  return useQuery({ queryKey: supportKeys.blocks, queryFn: supportApi.blocks, meta: { persist: false } })
}

/** Writes on one conversation; each refreshes the thread, the lists and the badges. */
export function useConversationMutations(id: string) {
  const qc = useQueryClient()
  const refresh = () => {
    qc.invalidateQueries({ queryKey: supportKeys.conversation(id) })
    qc.invalidateQueries({ queryKey: supportKeys.lists })
    qc.invalidateQueries({ queryKey: supportKeys.counts })
    qc.invalidateQueries({ queryKey: supportKeys.projects })
  }
  return {
    update: useMutation({
      mutationFn: (body: SupportConversationUpdate) => supportApi.update(id, body),
      onSuccess: (_row, body) => {
        refresh()
        if (body.block) qc.invalidateQueries({ queryKey: supportKeys.blocks })
      },
    }),
    reply: useMutation({
      mutationFn: (v: { body: string; status?: SupportStatus; files?: File[] }) => supportApi.reply(id, v.body, v.status, v.files),
      onSuccess: refresh,
    }),
    note: useMutation({ mutationFn: (body: string) => supportApi.note(id, body), onSuccess: refresh }),
    createTask: useMutation({ mutationFn: (body: { title?: string; board?: string }) => supportApi.createTask(id, body), onSuccess: refresh }),
  }
}

export function useSupportProjectMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: supportKeys.projects })
  return {
    create: useMutation({ mutationFn: supportApi.createProject, onSuccess: refresh }),
    update: useMutation({ mutationFn: (v: { slug: string; body: SupportProjectInput }) => supportApi.updateProject(v.slug, v.body), onSuccess: refresh }),
    setResendKey: useMutation({ mutationFn: (v: { slug: string; key: string | null }) => supportApi.setResendKey(v.slug, v.key), onSuccess: refresh }),
    rotate: useMutation({ mutationFn: (v: { slug: string; which: SupportSecret }) => supportApi.rotate(v.slug, v.which) }),
    setMode: useMutation({
      mutationFn: (v: { slug: string; mode: SupportMode }) => supportApi.setMode(v.slug, v.mode),
      onSuccess: () => {
        refresh()
        // Open threads show the sandbox banner from the project's mode.
        qc.invalidateQueries({ queryKey: supportKeys.all })
      },
    }),
  }
}

export function useSupportBlockMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: supportKeys.blocks })
  return {
    add: useMutation({ mutationFn: supportApi.addBlock, onSuccess: refresh }),
    remove: useMutation({ mutationFn: supportApi.removeBlock, onSuccess: refresh }),
  }
}
